'use client';

import type { DocumentStatus, VerificationStatus } from '@/types/firestore';
import { getVerificationVisualState } from '@/lib/constants';
import type { VerificationVisualState } from '@/lib/constants';

interface VerificationTagProps {
  documentStatus?: DocumentStatus;
  verificationStatus?: VerificationStatus;
  className?: string;
}

const DotIcon = () => (
  <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <circle cx="10" cy="10" r="5" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const XIcon = () => (
  <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

const STATE_CONFIG: Record<
  VerificationVisualState,
  { label: string; colorClass: string; icon: React.ReactNode | null }
> = {
  pending: {
    label: '미검증',
    colorClass: 'text-gray-500 bg-gray-50',
    icon: null,
  },
  in_review: {
    label: '검토 중',
    colorClass: 'text-orange-700 bg-orange-50',
    icon: <DotIcon />,
  },
  verified: {
    label: '검증 완료',
    colorClass: 'text-green-700 bg-green-50',
    icon: <CheckIcon />,
  },
  rejected: {
    label: '반려됨',
    colorClass: 'text-red-700 bg-red-50',
    icon: <XIcon />,
  },
};

export default function VerificationTag({
  documentStatus,
  verificationStatus,
  className,
}: VerificationTagProps) {
  const visualState = getVerificationVisualState(documentStatus, verificationStatus);
  const { label, colorClass, icon } = STATE_CONFIG[visualState];

  return (
    <span
      role="status"
      aria-label={`검증 상태: ${label}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colorClass} ${className || ''}`}
    >
      {icon && <span aria-hidden="true">{icon}</span>}
      <span>{label}</span>
    </span>
  );
}
